<!-- ══════════════════════ NAVBAR ══════════════════════ -->
<header
  class="sticky top-0 z-50 bg-white border-b border-gray-200"
  style="box-shadow:0 2px 6px rgba(0,0,0,0.15)"
>
  <!-- Full-width header with fixed side padding (left cluster hugs left, right cluster pinned right) —
       matches TSS, which does NOT cap the navbar at content width. Logo stays absolutely centered. -->
  <div class="max-w-[1920px] mx-auto px-4 lg:px-12 flex items-stretch h-[100px] relative">

    <!-- LEFT: hamburger + nav links -->
    <div class="flex items-center self-stretch">
      <button class="px-2 self-center text-[#292b2c] hover:text-[#ff0000] transition-colors"
              (click)="openDrawer()" aria-label="Menu">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" d="M4 6h16M4 12h16M4 18h16"/>
        </svg>
      </button>
      <!-- Nav links: text-only (no horizontal padding) + gap spacing, so the active red
           underline spans just the text width and sits at the navbar bottom — matches TSS. -->
      <nav class="hidden lg:flex items-stretch h-full ml-5 gap-8">
        @for (link of navLinks; track link.label) {
          <a [routerLink]="link.path"
             class="flex items-center h-full text-[23px] text-[#292b2c] font-semibold uppercase border-b-[3px] transition-colors duration-150 hover:text-[#ff0000]"
             [style.borderBottomColor]="isNavActive(link.path) ? '#ff0000' : 'transparent'"
          >{{ link.label }}</a>
        }
      </nav>
    </div>

    <!-- CENTER: logo — fills the navbar height on desktop; capped on mobile so the wide logo
         doesn't overlap the hamburger / right-hand icons on narrow screens. -->
    <div class="absolute inset-y-0 left-1/2 -translate-x-1/2 flex items-center lg:items-end pointer-events-none">
      <a routerLink="/" class="flex items-center lg:items-end select-none h-full pointer-events-auto">
        <img src="/assets/FINAL-LOGO-1024x1024.png" alt="Logo" class="h-[72px] lg:h-full w-auto object-contain"/>
      </a>
    </div>

    <!-- RIGHT: search + icon group -->
    <div class="flex items-stretch justify-end ml-auto">

      <!-- Mobile search icon (visible on mobile only) -->
      <button (click)="openSearch()"
              class="flex lg:hidden flex-col items-center px-1.5 text-[#292b2c] hover:text-[#ff0000] transition-colors self-stretch justify-center">
        <svg class="w-[22px] h-[22px]" fill="none" stroke="currentColor" stroke-width="1.7" viewBox="0 0 24 24">
          <circle cx="11" cy="11" r="7"/><path stroke-linecap="round" d="M21 21l-4.35-4.35"/>
        </svg>
      </button>

      <!-- Search bar (desktop) — the bar IS the live input; dropdown anchors under it (matches TSS) -->
      <div class="hidden lg:block relative self-center mr-1">
        <div class="flex items-center border rounded-full px-4 gap-2 w-[330px] h-[46px] bg-white transition-colors"
             [style.borderColor]="searchOpen() ? '#000' : '#282c3f'">
          <input [ngModel]="searchQuery()"
                 (ngModelChange)="searchQuery.set($event)"
                 (focus)="openSearch()"
                 (blur)="onSearchBlur()"
                 (keyup.enter)="submitSearch()"
                 type="text"
                 placeholder="What are you looking for?"
                 class="text-[14px] text-[#282c3f] placeholder:text-gray-400 flex-1 min-w-0 outline-none bg-transparent"/>
          <button (click)="submitSearch()" class="flex-shrink-0 flex items-center" aria-label="Search">
            <svg class="w-[18px] h-[18px] text-[#666]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="7"/><path stroke-linecap="round" d="M21 21l-4.35-4.35"/>
            </svg>
          </button>
        </div>

        <!-- Desktop dropdown — open while the bar is focused (shows recommendations + live results) -->
        @if (searchOpen()) {
          <div class="absolute top-[calc(100%+8px)] right-0 w-[400px] bg-white shadow-xl border border-gray-200 rounded-sm overflow-hidden z-[90]">
            <div class="max-h-[70vh] overflow-y-auto">
              <ng-container [ngTemplateOutlet]="searchSuggestionsList"></ng-container>
            </div>
          </div>
        }
      </div>

      <!-- Track order → My Account orders (desktop only, signed-in only) -->
      @if (auth.isAuthenticated()) {
      <a routerLink="/my-account/orders" aria-label="Track Order"
         class="group hidden lg:flex flex-col items-center px-1.5 lg:px-2.5 text-[#292b2c] hover:text-[#ff0000] transition-colors">
        <div class="flex flex-1 items-center">
          <svg class="w-[26px] h-[26px]" fill="none" stroke="currentColor" stroke-width="1.7" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M3 7h11v8H3zM14 10h4l3 3v2h-7z"/>
            <circle cx="7" cy="17.5" r="1.6"/><circle cx="17.5" cy="17.5" r="1.6"/>
          </svg>
        </div>
        <span class="w-full h-[3px] bg-[#ff0000] invisible group-hover:visible"></span>
      </a>
      }

      <!-- Account icon + dropdown -->
      <div class="group relative flex flex-col items-center px-1.5 lg:px-2.5"
           (mouseenter)="openAccountDropdown()"
           (mouseleave)="closeAccountDropdown()">
        <button (click)="goToAccount()" class="flex flex-1 items-center transition-colors group-hover:text-[#ff0000] text-[#292b2c] cursor-pointer" aria-label="Account">
          <svg class="w-[26px] h-[26px]" fill="none" stroke="currentColor" stroke-width="1.7" viewBox="0 0 24 24">
            <path stroke-linecap="round" d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
          </svg>
        </button>
        <span class="w-full h-[3px] bg-[#ff0000] invisible group-hover:visible"></span>

        <!-- Dropdown panel — account options only exist for signed-in users -->
        @if (accountDropdownOpen() && auth.isAuthenticated()) {
          <div class="absolute right-0 top-[calc(100%-3px)] min-w-[170px] bg-white shadow-lg border border-gray-100 z-[100] py-1">
            @for (item of accountMenuItems; track item.path) {
              <a [routerLink]="item.path"
                 (click)="closeAccountDropdown()"
                 class="block px-5 py-2.5 text-[14px] text-[#282c3f] hover:bg-gray-50 transition-colors whitespace-nowrap">
                {{ item.label }}
              </a>
            }
            <div class="border-t border-gray-100 mt-1 pt-1">
              <button (click)="logout()"
                 class="block w-full text-left px-5 py-2.5 text-[14px] text-[#282c3f] hover:bg-gray-50 transition-colors">
                Logout
              </button>
            </div>
          </div>
        }
      </div>

      <!-- Wishlist -->
      <a routerLink="/my-account/wishlist" aria-label="Wishlist"
         class="group relative flex flex-col items-center px-1.5 lg:px-2.5 text-[#292b2c] hover:text-[#ff0000] transition-colors">
        <div class="flex flex-1 items-center">
          <span class="relative inline-flex">
            <svg class="w-[26px] h-[26px]" fill="none" stroke="currentColor" stroke-width="1.7" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
            @if (wishlistCount > 0) {
              <span class="absolute -top-2 -right-2 bg-[#ff0000] text-white font-bold text-[9px] min-w-[15px] h-[15px] px-0.5 rounded-full flex items-center justify-center">{{ wishlistCount }}</span>
            }
          </span>
        </div>
        <span class="w-full h-[3px] bg-[#ff0000] invisible group-hover:visible"></span>
      </a>

      <!-- Cart -->
      <a routerLink="/cart" aria-label="Cart"
         class="group relative flex flex-col items-center px-1.5 lg:px-2.5 text-[#292b2c] hover:text-[#ff0000] transition-colors">
        <div class="flex flex-1 items-center">
          <span class="relative inline-flex">
            <svg class="w-[26px] h-[26px]" fill="none" stroke="currentColor" stroke-width="1.7" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4zM3 6h18M16 10a4 4 0 0 1-8 0"/>
            </svg>
            @if (cartCount > 0) {
              <span class="absolute -top-2 -right-2 bg-[#ff0000] text-white font-bold text-[9px] min-w-[15px] h-[15px] px-0.5 rounded-full flex items-center justify-center">
                {{ cartCount }}
              </span>
            }
          </span>
        </div>
        <span class="w-full h-[3px] bg-[#ff0000] invisible group-hover:visible"></span>
      </a>

    </div>
  </div>
</header>

<!-- ══════════════════════ SEARCH OVERLAY ══════════════════════ -->

<!-- Shared suggestion list — live catalog results + matching top searches; only rendered while typing -->
<ng-template #searchSuggestionsList>
  <!-- MATCHING PRODUCTS (live catalog) -->
  @if (searchResults().length) {
    <div class="px-4 pt-3 pb-1">
      <p class="text-[11px] font-bold text-[#94969f] uppercase tracking-wider mb-2">Products</p>
      <ul class="flex flex-col">
        @for (p of searchResults(); track p.slug) {
          <li>
            <button (click)="selectSuggestion('/product/' + p.slug)"
                    class="flex items-center gap-3 w-full text-left py-1.5 group">
              <img [src]="p.img" [alt]="p.name" class="w-9 h-11 object-cover object-top rounded-sm flex-shrink-0 bg-[#f5f5f5]"/>
              <span class="min-w-0">
                <span class="block text-[13px] text-[#282c3f] truncate group-hover:text-[#ff0000] transition-colors">{{ p.name }}</span>
                <span class="block text-[11px] text-[#94969f] truncate">{{ p.category }}</span>
              </span>
            </button>
          </li>
        }
      </ul>
    </div>
  }

  <!-- MATCHING TOP SEARCHES -->
  @if (filteredSuggestions.length) {
    <div class="px-4 py-3" [class.border-t]="searchResults().length" [class.border-gray-100]="searchResults().length">
      <p class="text-[11px] font-bold text-[#94969f] uppercase tracking-wider mb-2">Top Search Queries</p>
      <ul class="flex flex-col">
        @for (s of filteredSuggestions; track s.label) {
          <li>
            <button (click)="selectSuggestion(s.route)"
                    class="flex items-center gap-3 w-full text-left py-1.5 group">
              <svg class="w-[14px] h-[14px] text-[#c8c8c8] group-hover:text-[#ff0000] flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <circle cx="11" cy="11" r="7"/><path stroke-linecap="round" d="M21 21l-4.35-4.35"/>
              </svg>
              <span class="text-[13px] text-[#282c3f] group-hover:text-[#ff0000] transition-colors">{{ s.label }}</span>
            </button>
          </li>
        }
      </ul>
    </div>
  }

  <!-- "See all" footer + empty state (only once the user has actually typed) -->
  @if (hasQuery()) {
    @if (searchResults().length || filteredSuggestions.length) {
      <button (click)="submitSearch()"
              class="block w-full text-center px-4 py-3 border-t border-gray-100 text-[13px] font-semibold text-[#282c3f] hover:text-[#ff0000] transition-colors">
        See all results for "{{ searchQuery().trim() }}"
      </button>
    } @else {
      <div class="px-4 py-5 text-[13px] text-[#94969f] text-center">No results for "{{ searchQuery().trim() }}"</div>
    }
  }
</ng-template>

@if (searchOpen()) {
  <!-- Backdrop (click-out to close; no page dim — matches TSS dropdown).
       Sits BELOW the header (z-40 < header z-50) so the desktop dropdown, which lives
       inside the header's stacking context, can still paint above it. -->
  <div class="fixed inset-0 z-[40]" (click)="closeSearch()"></div>

  <!-- Mobile-only dropdown — full-width under navbar, with its own input (no visible search bar on mobile) -->
  <div class="lg:hidden fixed top-[150px] z-[90] left-2 right-2 bg-white shadow-xl border border-gray-200 rounded-sm overflow-hidden">

    <!-- Input row (mobile only) -->
    <div class="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
      <svg class="w-[18px] h-[18px] text-[#94969f] flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <circle cx="11" cy="11" r="7"/><path stroke-linecap="round" d="M21 21l-4.35-4.35"/>
      </svg>
      <input #searchInput
             [ngModel]="searchQuery()"
             (ngModelChange)="searchQuery.set($event)"
             (keyup.enter)="submitSearch()"
             type="text"
             placeholder="What are you looking for?"
             autofocus
             class="flex-1 text-[14px] text-[#282c3f] placeholder:text-[#94969f] outline-none bg-transparent"/>
      <button (click)="closeSearch()" class="p-0.5 text-[#94969f] hover:text-[#282c3f] transition-colors flex-shrink-0">
        <svg class="w-[18px] h-[18px]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
    </div>

    <div class="max-h-[70vh] overflow-y-auto">
      <ng-container [ngTemplateOutlet]="searchSuggestionsList"></ng-container>
    </div>
  </div>
}

<!-- ══════════════════════ DRAWER ══════════════════════ -->
<!-- Only rendered while open — guarantees it can never flash/animate open on page load/refresh. -->
@if (drawerOpen()) {

<!-- Backdrop -->
<div class="fixed inset-0 z-[60] bg-black/50"
     (click)="closeDrawer()"></div>

<!-- Sidenav panel — 576px matching TSS exact width -->
<div class="fixed top-0 left-0 z-[70] h-full w-[700px] max-w-[95vw] bg-white flex flex-col shadow-2xl ease-in-out overflow-hidden">

  <!-- ── HEADER: logo + login pill ── -->
  <div class="flex items-center justify-between px-4 py-3 flex-shrink-0 bg-white">
    <div class="flex items-center gap-3">
      <img src="/assets/FINAL-LOGO-1024x1024.png" alt="Logo"
           class="w-12 h-12 rounded-full object-contain border border-gray-100 flex-shrink-0"/>
      <!-- TSS-style pill button — reflects auth state -->
      @if (auth.isAuthenticated()) {
        <a routerLink="/my-account" (click)="closeDrawer()"
           class="text-[13px] font-semibold text-[#282c3f] hover:text-[#ff0000] transition-colors whitespace-nowrap">
          Hi, {{ auth.user()?.firstName || auth.user()?.name || 'Account' }}
        </a>
        <button (click)="logout(); closeDrawer()"
           class="border border-[#282c3f] rounded-full px-4 py-1.5 text-[13px] font-semibold text-[#282c3f] hover:bg-gray-50 transition-colors whitespace-nowrap">
          Logout
        </button>
      } @else {
        <a routerLink="/login" (click)="closeDrawer()"
           class="border border-[#282c3f] rounded-full px-4 py-1.5 text-[13px] font-semibold text-[#282c3f] hover:bg-gray-50 transition-colors whitespace-nowrap">
          Log In/Register
        </a>
      }
    </div>
    <button class="p-1.5 text-gray-400 hover:text-gray-700"
            (click)="closeDrawer()" aria-label="Close">
      <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
      </svg>
    </button>
  </div>

  <!-- ── TAB BAR: MEN | WOMEN | ACCESSORIES ── -->
  <div class="flex flex-shrink-0 border-b border-gray-200">
    @for (tab of drawerTabs; track tab) {
      <button
        class="flex-1 py-4 text-[15px] font-bold uppercase tracking-wider transition-colors"
        [class.text-[#282c3f]]="activeDrawerTab() === tab"
        [class.text-gray-400]="activeDrawerTab() !== tab"
        (click)="setDrawerTab(tab)">
        {{ tab }}
      </button>
    }
  </div>

  <!-- ── SCROLLABLE CONTENT ── -->
  <div class="flex-1 overflow-y-auto overscroll-contain">

    <!-- Featured carousels -->
    @for (section of currentContent.featured; track $index) {

      @if (section.name) {
        <!-- Section label — plain text link -->
        <div class="px-4 pt-3 pb-1">
          <span class="text-[13px] font-semibold text-[#282c3f]">{{ section.name }}</span>
        </div>
      } @else {
        <div class="h-2"></div>
      }

      <!-- Horizontal tile carousel (Swiper: drag + position-tracking pagination) -->
      <swiper-container class="drawer-rail block px-4 pb-1" slides-per-view="auto" space-between="12"
                        free-mode="true" grab-cursor="true" pagination="true"
                        pagination-clickable="true" pagination-dynamic-bullets="true">
        @for (tile of section.tiles; track tile.label) {
          <swiper-slide style="width:124px">
            <a [routerLink]="tile.path" (click)="closeDrawer()"
               class="flex flex-col items-center gap-1.5 w-[124px] no-underline">
              <div class="w-[124px] h-[124px] rounded-lg overflow-hidden bg-gray-100 border border-gray-100">
                <img [src]="tile.img" [alt]="tile.label"
                     class="w-full h-full object-cover" loading="lazy"/>
              </div>
              <span class="text-[13px] text-center text-[#282c3f] leading-tight w-full">{{ tile.label }}</span>
            </a>
          </swiper-slide>
        }
      </swiper-container>

      <div class="mx-4 border-b border-dashed border-gray-200"></div>
    }

    <!-- ── SHOP ALL (accordion wrapping only Categories, like TSS) ── -->
    <div class="border-t border-dashed border-gray-200">
      <button class="w-full flex items-center justify-between px-4 py-3.5 text-[15px] font-semibold text-[#282c3f] hover:bg-gray-50"
              (click)="toggleAccordion('categories')">
        <span>Shop All</span>
        <svg class="w-3.5 h-3.5 text-gray-500 transition-transform duration-200"
             [class.rotate-180]="isExpanded('categories')"
             fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
        </svg>
      </button>
      @if (isExpanded('categories')) {
        <!-- Categories grid inside Shop All -->
        <div class="border-t border-dashed border-gray-200 bg-gray-50">
          <div class="px-4 py-3 flex flex-wrap gap-3">
            @for (cat of drawerCategories; track cat.label) {
              <a [routerLink]="cat.path" (click)="closeDrawer()"
                 class="flex flex-col items-center gap-1 w-[106px] no-underline">
                <div class="w-[106px] h-[106px] rounded-lg overflow-hidden bg-white border border-gray-200">
                  <img [src]="cat.img" [alt]="cat.label"
                       class="w-full h-full object-cover" loading="lazy"/>
                </div>
                <span class="text-[13px] text-center text-[#282c3f] leading-tight">{{ cat.label }}</span>
              </a>
            }
          </div>
        </div>
      }
    </div>

    <!-- All Accessories — always-visible tiles like TSS -->
    <div class="border-t border-dashed border-gray-200 px-4 py-3">
      <p class="text-[13px] font-medium text-[#282c3f] mb-3">All Accessories</p>
      <swiper-container class="drawer-rail block" slides-per-view="auto" space-between="12"
                        free-mode="true" grab-cursor="true" pagination="true"
                        pagination-clickable="true" pagination-dynamic-bullets="true">
        @for (tile of currentContent.accessories; track tile.label) {
          <swiper-slide style="width:124px">
            <a [routerLink]="tile.path" (click)="closeDrawer()"
               class="flex flex-col items-center gap-1.5 w-[124px] no-underline">
              <div class="w-[124px] h-[124px] rounded-lg overflow-hidden bg-white border border-gray-200">
                <img [src]="tile.img" [alt]="tile.label"
                     class="w-full h-full object-cover" loading="lazy"/>
              </div>
              <span class="text-[13px] text-center text-[#282c3f] leading-tight">{{ tile.label }}</span>
            </a>
          </swiper-slide>
        }
      </swiper-container>
    </div>

    <!-- Official Merch -->
    <div class="border-t border-dashed border-gray-200">
      <button class="w-full flex items-center justify-between px-4 py-3.5 text-[15px] font-medium text-[#282c3f] hover:bg-gray-50"
              (click)="toggleAccordion('merch')">
        <span>Official Merch</span>
        <svg class="w-3.5 h-3.5 text-gray-400 transition-transform"
             [class.rotate-180]="isExpanded('merch')"
             fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
        </svg>
      </button>
      @if (isExpanded('merch')) {
        <div class="bg-gray-50 border-t border-dashed border-gray-200">
          @for (link of officialMerchLinks; track link.label) {
            <a [routerLink]="link.path" (click)="closeDrawer()"
               class="flex px-6 py-2.5 text-[13px] text-[#535665] hover:bg-gray-100 border-t border-dashed border-gray-200">
              {{ link.label }}
            </a>
          }
        </div>
      }
    </div>

    <!-- ── BOTTOM NAV LINKS ── -->
    <div class="border-t border-gray-200 mt-1">
      <a routerLink="/membership" (click)="closeDrawer()"
         class="flex items-center px-4 py-3.5 text-[13px] font-bold text-[#00a99d] tracking-wide hover:bg-gray-50 border-b border-dashed border-gray-200">
        MY MEMBERSHIP
      </a>
      <a routerLink="/gift-voucher" (click)="closeDrawer()"
         class="flex items-center px-4 py-3.5 text-[13px] font-bold text-[#282c3f] tracking-wide hover:bg-gray-50 border-b border-dashed border-gray-200">
        GIFT VOUCHERS
      </a>
      <a routerLink="/stores-near-me" (click)="closeDrawer()"
         class="flex items-center px-4 py-3.5 text-[15px] font-medium text-[#282c3f] hover:bg-gray-50 border-b border-dashed border-gray-200">
        Stores Near Me
      </a>
      <a routerLink="/track-order" (click)="closeDrawer()"
         class="flex items-center px-4 py-3.5 text-[15px] font-medium text-[#282c3f] hover:bg-gray-50 border-b border-dashed border-gray-200">
        Track My Order!
      </a>
      <button class="w-full flex items-center justify-between px-4 py-3.5 text-[15px] font-medium text-[#282c3f] hover:bg-gray-50"
              (click)="toggleAccordion('more')">
        <span>More</span>
        <svg class="w-3.5 h-3.5 text-gray-400 transition-transform"
             [class.rotate-180]="isExpanded('more')"
             fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
        </svg>
      </button>
      @if (isExpanded('more')) {
        <div class="bg-gray-50 border-t border-dashed border-gray-200">
          @for (link of [
            { label: 'About Us',   path: '/about-us'   },
            { label: 'Contact Us', path: '/contact-us' },
            { label: 'Careers',    path: '/careers'    },
            { label: 'FAQs',       path: '/faqs'       }
          ]; track link.label) {
            <a [routerLink]="link.path" (click)="closeDrawer()"
               class="flex px-6 py-2.5 text-[12px] text-[#535665] hover:bg-gray-100 border-t border-dashed border-gray-200">
              {{ link.label }}
            </a>
          }
        </div>
      }
    </div>
    <div class="h-8"></div>
  </div>
</div>

}
